/**
 * Marker sebagai benda yang bisa dipegang, bukan garis yang cuma tergambar.
 *
 * Sebelum ini marker hanya bisa disunting di layar korelasi, dan hanya yang
 * sudah ada: menggeser, mengganti nama, menghapus. Memasang yang pertama
 * mustahil dari mana pun — sebuah nama baru hanya bisa lahir lewat impor atau
 * lewat sumur lain yang kebetulan sudah memilikinya. Di layar Log, tempat
 * seorang petrofisikawan sebenarnya memutuskan letak sebuah top, marker sama
 * sekali tidak bisa disentuh: ia digambar, dan itu saja.
 *
 * Yang ada di sini dipakai kedua layar supaya keduanya menanyakan hal yang
 * sama dengan cara yang sama — satu dialog, satu lapisan pegangan, dan satu
 * tempat aturan mainnya ditulis.
 */
import { useState, type ReactNode } from "react";

import { api } from "../api";
import { useAsync } from "../hooks";
import { PALETTE } from "../lib/correlation";
import type { Marker } from "../types";
import { ColorPicker } from "./ColorPicker";
import {
  Button,
  Dropdown,
  Loading,
  Modal,
  Notice,
  NumberField,
  TextField,
} from "./ui";

/** Satu baris dari `/api/projects/<id>/marker-names/`. */
interface MarkerName {
  name: string;
  wells: number;
  depth: number | null;
}

const CONFIDENCE: { value: Marker["confidence"]; label: string; hint: string }[] = [
  { value: "high", label: "High", hint: "picked from the log" },
  { value: "medium", label: "Medium", hint: "there but blurred" },
  { value: "low", label: "Low", hint: "predicted" },
];

const SOURCE: { value: Marker["source"]; label: string }[] = [
  { value: "log", label: "Log" },
  { value: "core", label: "Log + core" },
  { value: "seismic", label: "Seismik" },
];

/** Nilai penanda "bukan salah satu yang sudah ada" pada pemilih nama. */
const NEW_NAME = " new";

/**
 * Kedalaman yang dipetik dari layar, dibulatkan ke sentimeter.
 *
 * Sebuah klik memulangkan pecahan tinggi panel dikali rentang jendela, dan itu
 * keluar sebagai 2220,202864756447 m. Angka itu bukan hanya jelek dibaca — ia
 * mengaku tahu letak sebuah top sampai sepersekian mikron, padahal pada 1:200
 * satu piksel saja sudah lima sentimeter batuan.
 */
export const pickedDepth = (md: number) => Math.round(md * 100) / 100;

export interface MarkerDraft {
  /** Ada bila sedang menyunting marker yang sudah terpasang. */
  id?: number;
  name?: string;
  md: number;
  color?: string;
  confidence?: Marker["confidence"];
  source?: Marker["source"];
}

/**
 * Memasang marker baru, atau menyunting yang sudah terpasang.
 *
 * Nama dipilih dari yang sudah dipakai proyek ini lebih dulu, dan mengetik
 * nama baru butuh satu langkah tambahan. Urutannya disengaja: marker berguna
 * justru karena namanya sama persis di semua sumur — itulah yang disambung
 * garis tali di layar korelasi — dan kotak teks kosong mengundang
 * "Top Menggala", "Top menggala" dan "TOP MENGGALA" hidup berdampingan
 * sebagai tiga lapisan yang tidak akan pernah bertemu.
 */
export function MarkerDialog({
  projectId,
  wellId,
  wellName,
  draft,
  onClose,
  onSaved,
}: {
  projectId: number;
  wellId: number;
  wellName: string;
  draft: MarkerDraft;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const editing = draft.id !== undefined;
  const catalogue = useAsync(
    (signal) =>
      api.get<{ markers: MarkerName[] }>(
        `/api/projects/${projectId}/marker-names/`,
        undefined,
        signal,
      ),
    [projectId],
  );

  const [name, setName] = useState(draft.name ?? "");
  const [fresh, setFresh] = useState("");
  const [md, setMd] = useState(draft.md);
  const [color, setColor] = useState(draft.color || PALETTE[0]);
  const [confidence, setConfidence] = useState<Marker["confidence"]>(draft.confidence ?? "high");
  const [source, setSource] = useState<Marker["source"]>(draft.source ?? "log");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const known = catalogue.data?.markers ?? [];
  const naming = name === NEW_NAME || (!catalogue.loading && !editing && known.length === 0);
  const finalName = (naming ? fresh : name).trim();
  const shared = known.find((row) => row.name === finalName)?.wells;

  const save = async () => {
    if (!finalName) {
      setError("A marker must have a name.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = { well: wellId, name: finalName, md, color, confidence, source };
      if (editing) {
        await api.patch(`/api/markers/${draft.id}/`, body);
        onSaved(`${finalName} updated on ${wellName}.`);
      } else {
        await api.post("/api/markers/", body);
        onSaved(`${finalName} set on ${wellName} at ${md.toFixed(1)} m.`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit marker — ${wellName}` : `Set marker — ${wellName}`}
      onClose={onClose}
      width={430}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!finalName} onClick={save}>
            {editing ? "Simpan" : "Pasang"}
          </Button>
        </>
      }
    >
      {catalogue.loading && <Loading label="Reading the marker list…" />}

      {!naming && known.length > 0 && (
        <Dropdown
          label="Name"
          value={name}
          options={[
            ...(name ? [] : [{ value: "", label: "— pick a marker —" }]),
            ...known.map((row) => ({
              value: row.name,
              label: row.name,
              hint: `${row.wells} wells`,
            })),
            { value: NEW_NAME, label: "Marker baru…", hint: "a name of your own" },
          ]}
          onChange={setName}
          width="100%"
          compact
        />
      )}

      {naming && (
        <>
          <TextField
            label="New marker name"
            value={fresh}
            onChange={setFresh}
            placeholder="mis. Top Bekasap"
            autoFocus
          />
          {known.length > 0 && (
            <Button variant="ghost" onClick={() => setName("")}>
              Back to the list
            </Button>
          )}
        </>
      )}

      <NumberField label="Depth" unit="m MD" value={md} step={0.1} onChange={setMd} />

      <div style={{ display: "flex", gap: 10 }}>
        <Stacked label="Confidence">
          <Dropdown
            value={confidence}
            options={CONFIDENCE}
            onChange={setConfidence}
            width="100%"
            compact
          />
        </Stacked>
        <Stacked label="Sumber">
          <Dropdown value={source} options={SOURCE} onChange={setSource} width="100%" compact />
        </Stacked>
      </div>
      {/* Barisnya sendiri. Bertiga melintang pada lebar 430 px membuat label
          "Sumber" menimpa kotak di sebelahnya dan memotong "Warna" separuh. */}
      <ColorPicker value={color} onChange={setColor} label="Colour" />

      {!editing && shared !== undefined && (
        <Notice tone="info">
          {finalName} is already used on {shared} wells. It is exactly that same name
          that ties the correlation line together on the correlation screen.
        </Notice>
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </Modal>
  );
}

/**
 * Label di atas kendalinya, bukan di sebelahnya.
 *
 * `Dropdown` menaruh labelnya sebaris di kiri, dan itu benar untuk satu
 * kendali yang membentang. Dua berdampingan di dalam dialog selebar 430 px
 * membuat label kedua duduk menimpa kotak pertama — labelnya memakan lebar
 * yang tetap sementara tombolnya meminta seratus persen dari sisanya.
 */
function Stacked({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
      {children}
    </div>
  );
}

/**
 * Lapisan pegangan marker di atas lembar log.
 *
 * Garis putus-putusnya tetap digambar oleh track — track yang tahu pemetaan
 * kedalamannya. Yang ditambahkan di sini hanyalah yang bisa dipegang: label
 * bernama di tepi kiri, dan pita setinggi sebelas piksel yang menerima seretan
 * dan klik kanan. Pitanya jauh lebih tebal daripada garisnya, sebab garis
 * setebal satu piksel adalah sasaran yang praktis mustahil dikenai tetikus,
 * dan pada skala 1:5000 satu piksel itu tiga meter batuan.
 */
export function MarkerHandles({
  markers,
  top,
  base,
  mayWrite,
  onDrag,
  onMenu,
}: {
  markers: { id: number; name: string; md: number; color: string }[];
  top: number;
  base: number;
  mayWrite: boolean;
  /** Dipanggil selama seretan berjalan, dan sekali lagi ketika dilepas. */
  onDrag: (id: number, md: number, done: boolean) => void;
  onMenu: (event: { clientX: number; clientY: number }, id: number) => void;
}) {
  const span = base - top || 1;
  const [dragging, setDragging] = useState<number | null>(null);

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 }}>
      {markers.map((marker) => {
        const fraction = (marker.md - top) / span;
        if (fraction < 0 || fraction > 1) return null;
        return (
          <div
            key={marker.id}
            data-marker={marker.name}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${fraction * 100}%`,
              height: 11,
              marginTop: -5.5,
              display: "flex",
              alignItems: "center",
              pointerEvents: mayWrite ? "auto" : "none",
              cursor: mayWrite ? "ns-resize" : "default",
              background: dragging === marker.id ? "var(--accent-wash)" : "transparent",
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onMenu(event, marker.id);
            }}
            onPointerDown={(event) => {
              if (!mayWrite || event.button !== 0) return;
              // Ditahan di sini supaya tidak sampai ke lembar di belakangnya,
              // yang akan menafsirkan seretan yang sama sebagai zoom kotak.
              event.preventDefault();
              event.stopPropagation();
              const sheet = event.currentTarget.parentElement!.getBoundingClientRect();
              setDragging(marker.id);
              const depthAt = (clientY: number) =>
                top + ((clientY - sheet.top) / sheet.height) * span;
              const move = (moveEvent: PointerEvent) =>
                onDrag(marker.id, depthAt(moveEvent.clientY), false);
              const up = (upEvent: PointerEvent) => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
                setDragging(null);
                onDrag(marker.id, depthAt(upEvent.clientY), true);
              };
              window.addEventListener("pointermove", move);
              window.addEventListener("pointerup", up);
            }}
          >
            <span
              style={{
                marginLeft: 3,
                padding: "1px 5px",
                borderRadius: 3,
                background: "var(--panel)",
                border: `1px solid ${marker.color}`,
                font: "500 9px var(--font-mono)",
                color: "var(--ink-2)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {marker.name} {marker.md.toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Aksi marker yang dipakai kedua layar, supaya keduanya gagal dengan cara yang sama. */
export function markerActions(
  reload: () => void,
  toast: (message: string, tone?: "ok" | "error") => void,
) {
  const guard = async (work: () => Promise<string>) => {
    try {
      toast(await work());
      reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };
  return {
    move: (id: number, name: string, md: number) =>
      guard(async () => {
        await api.patch(`/api/markers/${id}/`, { md });
        return `${name} moved to ${md.toFixed(1)} m.`;
      }),
    remove: (id: number, name: string, wellName: string) =>
      guard(async () => {
        await api.delete(`/api/markers/${id}/`);
        return `${name} taken off ${wellName}.`;
      }),
    /**
     * `wells` bukan pilihan: tanpanya tidak terjadi apa-apa.
     *
     * Server memutar `request.data["wells"]`, jadi permintaan yang tidak
     * membawanya memutari daftar kosong, membuat nol marker, dan pulang
     * dengan 200 — layar lalu melaporkan "dipasang di 0 sumur lain" dengan
     * nada seolah itu jawaban, bukan kegagalan. Sumurnya juga tidak boleh
     * dipilih di sini: layar korelasi bermaksud sumur yang sedang tergambar
     * di panel, dan layar log bermaksud seluruh sumur proyek.
     */
    applyToAll: (projectId: number, name: string, md: number, wells: number[]) =>
      guard(async () => {
        const result = await api.post<{ created: number[] }>("/api/markers/apply-to-wells/", {
          project: projectId,
          name,
          md,
          wells,
        });
        return `${name} set on ${result.created.length} other wells.`;
      }),
  };
}
